import React, { useRef, useCallback, useMemo, useEffect, useState, lazy, Suspense } from 'react';
import { updateSyncErrors, touch } from 'redux-form';
import uniq from 'lodash/uniq';
import flattenDeep from 'lodash/flattenDeep';
import { useMonaco } from '@monaco-editor/react';
import { isIterableArray } from 'services/helpers';
import './schema_input.scss';
import { assertions } from 'components/provider/conditions/constants';
import { Icon } from 'components/ui';
import { useLock } from 'contexts/lock_ressource/use_lock';
import { useWorkflow } from 'contexts/workflow/use_workflow';
import Alert from 'components/ui/alert';
import Loader from 'components/ui/loader';
import { useSchemaForm } from 'contexts/schema_form/context';

const MonacoEditorLazy = lazy(() => import('@monaco-editor/react'));

const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

const getTranslationSuggestions = (translations) => {
  const allTranslations = uniq(flattenDeep(Object.keys(translations).map((locale) => Object.keys(translations[locale]))));
  return allTranslations.map((key) => ({
    label: key,
    insertText: key,
  }));
};

const SchemaInput = ({ input, label, height = '70vh', availableTranslations, availableData, meta, onError, onFullScreen, slug }) => {
  const editorRef = useRef(null);
  const monaco = useMonaco();
  const workflowDataCompletionItemProvider = useRef(null);
  const assertionCompletionItemProvider = useRef(null);
  const [fullScreen, setFullScreen] = useState(false);
  const { canEdit } = useLock();
  const { workflow } = useWorkflow();
  const { saveSchema } = useSchemaForm();

  const handleEditorDidMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor;
      formatDocument();
      editor.onDidChangeModelContent(
        debounce(() => {
          formatDocument();
        }, 1000),
      );
      editor.addAction({
        id: 'save-file',
        label: 'Save File',
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
        run: (editor) => {
          if (saveSchema) {
            saveSchema(false);
          }
        },
      });
    },
    [formatDocument],
  );

  const formatDocument = useCallback(() => {
    const editor = editorRef.current;
    const model = editor.getModel();
    if (model) {
      const content = model.getValue();
      try {
        JSON.parse(content);
        editor.getAction('editor.action.formatDocument').run();
      } catch (error) {}
    }
  }, []);

  const translationsSuggestions = useMemo(() => (availableTranslations ? getTranslationSuggestions(availableTranslations) : []), [availableTranslations]);

  const availableDataSuggestions = useMemo(() => (availableData ? availableData.map((data) => ({ label: data, insertText: data })) : []), [availableData]);

  const availableAssertions = useMemo(
    () =>
      assertions.map(({ assertion }) => ({
        label: assertion,
        insertText: assertion,
      })),
    [],
  );

  const handleEditorValidation = (markers) => {
    // model markers
    markers.forEach((marker) => console.log('onValidate:', marker));
    const errors = markers.map((marker) => `${marker.message} at line ${marker.startLineNumber}`);
    if (errors.length === 0) {
      meta && meta.dispatch(updateSyncErrors(meta.form, { [input.name]: null }));
      return;
    }
    meta && meta.dispatch(touch(meta.form, input.name));
    meta && meta.dispatch(updateSyncErrors(meta.form, { [input.name]: errors.join(', ') }));
    onError && onError(errors);
  };

  useEffect(() => {
    if (monaco) {
      assertionCompletionItemProvider.current = monaco.languages.registerCompletionItemProvider('json', {
        triggerCharacters: ['"'],

        provideCompletionItems: function (model, position) {
          const textUntilPosition = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          if (/^.*"assertion"\s*:\s*"$/.test(textUntilPosition)) {
            return {
              suggestions: availableAssertions.map((assertion) => ({
                ...assertion,
                kind: monaco.languages.CompletionItemKind.Value,
                range: {
                  startLineNumber: position.lineNumber,
                  startColumn: position.column,
                  endLineNumber: position.lineNumber,
                  endColumn: position.column,
                },
              })),
            };
          }

          return { suggestions: [] };
        },
      });
      workflowDataCompletionItemProvider.current = monaco.languages.registerCompletionItemProvider('json', {
        triggerCharacters: [':'],
        provideCompletionItems: function (model, position) {
          const textUntilPosition = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: position.column - 2,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          if (textUntilPosition === 't:' && isIterableArray(translationsSuggestions)) {
            return {
              suggestions: translationsSuggestions.map((suggestion) => ({
                ...suggestion,
                kind: monaco.languages.CompletionItemKind.Text,
              })),
            };
          }
          if (textUntilPosition === 'v:' && isIterableArray(availableDataSuggestions)) {
            return {
              suggestions: availableDataSuggestions.map((suggestion) => ({
                ...suggestion,
                kind: monaco.languages.CompletionItemKind.Text,
                additionalTextEdits: [
                  {
                    range: {
                      startLineNumber: position.lineNumber,
                      startColumn: position.column - 2,
                      endLineNumber: position.lineNumber,
                      endColumn: position.column,
                    },
                    text: '',
                  },
                ],
              })),
            };
          }
          return { suggestions: [] };
        },
      });
      return () => {
        assertionCompletionItemProvider.current.dispose();
        workflowDataCompletionItemProvider.current.dispose();
      };
    }
  }, [monaco]);

  const handleFullScreen = () => {
    setFullScreen(!fullScreen);
    onFullScreen(fullScreen);
  };

  return (
    <div className="monaco-container">
      <button className={`schema-fullscreen-button fullscreen-btn ${fullScreen ? 'active' : ''}`} onClick={handleFullScreen}>
        {fullScreen ? <Icon name="BiExitFullscreen" /> : <Icon name="BiFullscreen" />}
      </button>
      <div className={fullScreen ? 'fullscreen' : ''}>
        {fullScreen && <div className="schema-slug-item">{slug}</div>}
        {workflow && !canEdit && <Alert className="schema-read-only-indicator" color="warning">Lecture seule</Alert>}
        <Suspense fallback={<Loader />}>
          <MonacoEditorLazy
            className="json-editor"
            height={height}
            defaultLanguage="json"
            theme="vs-dark"
            defaultValue={input.value ? JSON.stringify(input.value, null, 2) : ''}
            onMount={handleEditorDidMount}
            onValidate={handleEditorValidation}
            options={{
              tabSize: 2,
              readOnly: workflow ? !canEdit : false,
              wordWrap: 'on',
            }}
            onChange={(value) => {
              try {
                input.onChange(JSON.parse(value));
              } catch (e) {}
            }}
          />
        </Suspense>
      </div>
    </div>
  );
};

export default SchemaInput;
